// ============================================
// Hemologix Seed Data: Longevity Products (Tier 3)
// Catalog Numbers 62-74
// ============================================

import type { Product } from '../../types/formulary';

export const SEED_LONGEVITY: Product[] = [
  // --- 62. NAD+ SubQ ---
  {
    id: 'nad-subq',
    catalogNumber: 62,
    name: 'NAD+ SubQ',
    ingredients: 'Nicotinamide adenine dinucleotide (NAD+)',
    category: 'longevity',
    subcategory: 'nad_cellular',
    tier: 3,
    route: 'subq',
    dosage: '100-200mg subcutaneous injection 2-3x/week',
    timing: 'AM_ENERGY',
    additionalTimings: ['PM_RECOVERY'],
    triggers: [
      {
        biomarker: 'NAD+ levels',
        condition: 'below_functional',
        description: 'Suboptimal NAD+ levels or age-related decline',
      },
    ],
    targetBiomarkers: ['NAD+ levels', 'intracellular NAD+'],
    targetGoals: ['longevity', 'energy'],
    pairsWith: ['bpc-157', 'rapamycin', 'epithalon'],
    contraindications: ['active malignancy (theoretical)'],
    drugInteractions: [],
    allergenFlags: [],
    pregnancyCategory: 'X — not studied, avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['NAD+ levels (intracellular)', 'CBC', 'CMP at baseline and 8 weeks'],
    evidenceLevel: 'moderate',
    evidenceSummary:
      'Subcutaneous NAD+ bypasses first-pass metabolism, achieving higher intracellular levels than oral NMN/NR. Clinical data from longevity clinics shows improved energy, recovery, and mitochondrial function markers. Randomized controlled trials are limited but growing.',
    citations: [
      'Rajman L et al. Therapeutic Potential of NAD-Boosting Molecules. Cell Metab. 2018;27(3):529-547.',
      'Yoshino J et al. NAD+ Intermediates: The Biology and Therapeutic Potential. Cell Metab. 2018;27(3):513-528.',
    ],
    gender: 'any',
    ageMin: 30,
    clinicalNotes:
      'Administer subcutaneously in abdominal fat pad. Rotate injection sites. Some patients report flushing or mild nausea at higher doses — titrate up from 100mg. Store refrigerated.',
    rationale:
      'NAD+ SubQ is recommended to restore declining cellular NAD+ levels, supporting mitochondrial function, DNA repair, and sirtuin activity.',
    pricing: {
      manufacturingCost: '$25-40/vial',
      supplierCost: '$150-250/mo',
      monthlyPrice: '$499/mo',
    },
    status: 'active',
    sortOrder: 62,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 63. NAD+ IV Infusion ---
  {
    id: 'nad-iv-infusion',
    catalogNumber: 63,
    name: 'NAD+ IV Infusion',
    ingredients: 'Nicotinamide adenine dinucleotide (NAD+) in sterile saline',
    category: 'longevity',
    subcategory: 'nad_cellular',
    tier: 3,
    route: 'iv',
    dosage: '250-500mg IV infusion over 2-4 hours',
    timing: 'AM_ENERGY',
    triggers: [
      {
        biomarker: 'NAD+ levels',
        condition: 'below_standard',
        description: 'Severely depleted NAD+ or acute repletion needed',
      },
    ],
    targetBiomarkers: ['NAD+ levels', 'intracellular NAD+'],
    targetGoals: ['longevity', 'energy', 'cognition'],
    pairsWith: ['nad-subq', 'glutathione-iv'],
    contraindications: ['uncontrolled hypertension', 'active infection at infusion site'],
    drugInteractions: [],
    allergenFlags: [],
    pregnancyCategory: 'X — not studied, avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Vitals during infusion', 'NAD+ levels pre/post', 'CMP'],
    evidenceLevel: 'moderate',
    evidenceSummary:
      'IV NAD+ provides maximal bioavailability for acute NAD+ repletion. Used in addiction recovery (Haidut protocol) and longevity medicine. Infusion must be slow to avoid chest tightness, nausea, and cramping. Provider-administered in clinical setting only.',
    citations: [
      'Grant R et al. A Pilot Study Investigating Changes in the Human Plasma and Urine NAD+ Metabolome During a 6 Hour Intravenous Infusion of NAD+. Front Aging Neurosci. 2019;11:257.',
    ],
    gender: 'any',
    ageMin: 25,
    clinicalNotes:
      'Provider-administered only. Infuse slowly over 2-4 hours; rapid infusion causes chest pressure, nausea, and abdominal cramping. Pre-hydrate patient. Monitor vitals q30min during infusion. Typically performed as 1-4 session loading protocol.',
    rationale:
      'NAD+ IV infusion is indicated for acute NAD+ repletion in patients with severe depletion or as a loading dose before transitioning to SubQ maintenance.',
    pricing: {
      manufacturingCost: '$40-80/session',
      supplierCost: '$200-350/session',
      monthlyPrice: '$499/mo',
    },
    status: 'active',
    sortOrder: 63,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 64. Rapamycin (Sirolimus) ---
  {
    id: 'rapamycin',
    catalogNumber: 64,
    name: 'Rapamycin (Sirolimus)',
    ingredients: 'Sirolimus (rapamycin)',
    category: 'longevity',
    subcategory: 'mtor_longevity',
    tier: 3,
    route: 'oral',
    dosage: '3-6mg once weekly (pulsed dosing)',
    timing: 'AM_ENERGY',
    triggers: [
      {
        biomarker: 'mTOR pathway activation',
        condition: 'any_suboptimal',
        description: 'Longevity optimization — mTOR inhibition protocol',
      },
    ],
    targetBiomarkers: ['hs-CRP', 'Fasting Insulin', 'Fasting Glucose', 'lipid panel'],
    targetGoals: ['longevity', 'immune'],
    pairsWith: ['metformin-er', 'nad-subq'],
    contraindications: [
      'active infection',
      'immunosuppressed patients',
      'organ transplant recipients (different dosing protocol)',
      'severe hepatic impairment',
      'pregnancy or planning pregnancy',
    ],
    drugInteractions: [
      'CYP3A4 inhibitors (ketoconazole, erythromycin, grapefruit) — increase levels',
      'CYP3A4 inducers (rifampin, phenytoin) — decrease levels',
      'live vaccines — avoid during use',
    ],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: [
      'Lipid panel (LDL, triglycerides) at baseline and q3mo',
      'Fasting glucose and insulin q3mo',
      'CBC with differential q3mo',
      'CMP (hepatic/renal) at baseline and q6mo',
      'Sirolimus trough levels if clinically indicated',
    ],
    evidenceLevel: 'moderate',
    evidenceSummary:
      'Rapamycin is the most replicated longevity intervention in animal models, extending lifespan across multiple species. Pulsed weekly dosing (3-6mg) avoids immunosuppressive trough levels while providing mTOR inhibition benefits. Human data includes the PEARL trial (improved immune function in elderly) and growing off-label longevity use. Lipid and glucose elevations are common but manageable.',
    citations: [
      'Mannick JB et al. mTOR inhibition improves immune function in the elderly. Sci Transl Med. 2014;6(268):268ra179.',
      'Bitto A et al. Transient rapamycin treatment can increase lifespan and healthspan in middle-aged mice. eLife. 2016;5:e16351.',
      'Kraig E et al. A randomized control trial to establish the feasibility and safety of rapamycin treatment in an older human cohort. Exp Gerontol. 2018;105:53-58.',
    ],
    gender: 'any',
    ageMin: 35,
    clinicalNotes:
      'Pulsed weekly dosing (NOT daily immunosuppressive dosing). Take on same day each week, preferably AM with fat-containing meal for absorption. Common side effects: mouth sores (aphthous ulcers), mild lipid elevation, glucose elevation. Hold if active infection. Avoid live vaccines.',
    rationale:
      'Rapamycin is prescribed at pulsed weekly dosing for mTOR pathway inhibition, the most evidence-backed pharmacological longevity intervention. Monitoring lipids, glucose, and CBC is essential.',
    pricing: {
      manufacturingCost: '$3-8/tablet',
      supplierCost: '$30-60/mo',
      monthlyPrice: '$599/mo',
    },
    status: 'active',
    sortOrder: 64,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 65. Methylene Blue ---
  {
    id: 'methylene-blue',
    catalogNumber: 65,
    name: 'Methylene Blue',
    ingredients: 'Methylene blue (methylthioninium chloride), pharmaceutical grade',
    category: 'longevity',
    subcategory: 'mitochondrial_cognitive',
    tier: 3,
    route: 'oral',
    dosage: '0.5-1mg/kg orally once daily',
    timing: 'AM_ENERGY',
    triggers: [
      {
        biomarker: 'cognitive function',
        condition: 'any_suboptimal',
        description: 'Mitochondrial support, cognitive optimization',
      },
    ],
    targetBiomarkers: ['mitochondrial function markers', 'cognitive assessment scores'],
    targetGoals: ['cognition', 'longevity', 'energy'],
    pairsWith: ['nad-subq', 'semax', 'cerebrolysin'],
    contraindications: [
      'G6PD deficiency (risk of hemolytic anemia)',
      'concurrent SSRI use (serotonin syndrome — life-threatening)',
      'concurrent MAOI use (serotonin syndrome)',
      'concurrent SNRI use',
      'renal impairment (eGFR <30)',
      'pregnancy',
    ],
    drugInteractions: [
      'SSRIs (fluoxetine, sertraline, paroxetine, citalopram, escitalopram) — CONTRAINDICATED, serotonin syndrome risk',
      'MAOIs (phenelzine, tranylcypromine, selegiline) — CONTRAINDICATED, serotonin syndrome risk',
      'SNRIs (venlafaxine, duloxetine) — CONTRAINDICATED, serotonin syndrome risk',
      'Tramadol — serotonin syndrome risk',
      'Fentanyl — serotonin syndrome risk',
      'Dextromethorphan — serotonin syndrome risk',
    ],
    allergenFlags: [],
    pregnancyCategory: 'X',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: [
      'G6PD screening REQUIRED before initiation',
      'Medication review for serotonergic drugs',
      'Methemoglobin level if symptoms arise',
      'Renal function (eGFR)',
    ],
    evidenceLevel: 'moderate',
    evidenceSummary:
      'Methylene blue acts as an alternative electron carrier in the mitochondrial electron transport chain, bypassing Complex I/III defects. Demonstrated neuroprotective effects in preclinical models and small human trials for cognitive decline. FDA-approved for methemoglobinemia at higher doses. Longevity/cognitive use at low doses (0.5-1mg/kg) shows improved mitochondrial respiration and memory consolidation.',
    citations: [
      'Rojas JC et al. Neurometabolic mechanisms for memory enhancement and neuroprotection of methylene blue. Prog Neurobiol. 2012;96(1):32-45.',
      'Rodriguez P et al. Methylene blue and Alzheimer disease. Eur Neuropsychopharmacol. 2017;27(2):142-155.',
    ],
    gender: 'any',
    ageMin: 30,
    clinicalNotes:
      'CRITICAL: Screen for G6PD deficiency before prescribing. Review ALL current medications for serotonergic interactions — this is a life-threatening contraindication. Urine and stool will turn blue/green (counsel patient). Start at 0.5mg/kg and titrate. Take AM with food.',
    rationale:
      'Methylene blue supports mitochondrial electron transport chain function and has neuroprotective properties for cognitive optimization at low doses.',
    pricing: {
      manufacturingCost: '$2-5/bottle',
      supplierCost: '$20-40/mo',
      monthlyPrice: '$399/mo',
    },
    status: 'active',
    sortOrder: 65,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 66. BPC-157 ---
  {
    id: 'bpc-157',
    catalogNumber: 66,
    name: 'BPC-157',
    ingredients: 'Body Protection Compound-157 (pentadecapeptide)',
    category: 'longevity',
    subcategory: 'joint_musculoskeletal',
    tier: 3,
    route: 'subq',
    dosage: '250-500mcg subcutaneous 2x/day',
    timing: 'AM_ENERGY',
    additionalTimings: ['PM_RECOVERY'],
    triggers: [
      {
        biomarker: 'inflammatory markers',
        condition: 'above_functional',
        description: 'Tissue repair, tendon/ligament injury, gut healing',
      },
    ],
    targetBiomarkers: ['hs-CRP', 'ESR'],
    targetGoals: ['longevity', 'body_composition'],
    pairsWith: ['nad-subq', 'ghk-cu', 'thymosin-alpha-1'],
    contraindications: ['active malignancy (theoretical — promotes angiogenesis)', 'pregnancy'],
    drugInteractions: [],
    allergenFlags: [],
    pregnancyCategory: 'X — not studied, avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Clinical symptom tracking', 'hs-CRP at baseline and 8 weeks'],
    evidenceLevel: 'emerging',
    evidenceSummary:
      'BPC-157 is a synthetic pentadecapeptide derived from gastric juice. Extensive preclinical data demonstrates accelerated healing of tendons, ligaments, muscle, gut mucosa, and bone. Mechanisms include upregulation of growth factor receptors, nitric oxide synthesis, and angiogenesis. No published RCTs in humans, but widespread clinical use in regenerative medicine with favorable safety profiles.',
    citations: [
      'Sikiric P et al. Brain-gut Axis and Pentadecapeptide BPC 157: Theoretical and Practical Implications. Curr Neuropharmacol. 2016;14(8):857-865.',
      'Seiwerth S et al. BPC 157 and Standard Angiogenic Growth Factors. Curr Pharm Des. 2018;24(18):1972-1989.',
    ],
    gender: 'any',
    ageMin: 18,
    clinicalNotes:
      'Can be administered subcutaneously near injury site or systemically in abdominal fat. Also available as oral capsule for GI healing (same dose). Typical cycle: 4-12 weeks. Reconstitute lyophilized powder with bacteriostatic water. Store refrigerated after reconstitution.',
    rationale:
      'BPC-157 is recommended for accelerated tissue repair and gut healing via upregulation of growth factor pathways and nitric oxide synthesis.',
    pricing: {
      manufacturingCost: '$15-30/vial',
      supplierCost: '$80-150/mo',
      monthlyPrice: '$499/mo',
    },
    status: 'active',
    sortOrder: 66,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 67. Thymosin Alpha-1 ---
  {
    id: 'thymosin-alpha-1',
    catalogNumber: 67,
    name: 'Thymosin Alpha-1',
    ingredients: 'Thymosin alpha-1 (thymalfasin)',
    category: 'longevity',
    subcategory: 'immune_inflammation',
    tier: 3,
    route: 'subq',
    dosage: '1.6mg subcutaneous 2x/week',
    timing: 'AM_ENERGY',
    triggers: [
      {
        biomarker: 'WBC',
        condition: 'any_suboptimal',
        description: 'Immune dysfunction, chronic infections, post-viral syndromes',
      },
    ],
    targetBiomarkers: ['WBC', 'lymphocyte subsets (CD4/CD8)', 'NK cell activity'],
    targetGoals: ['immune', 'longevity'],
    pairsWith: ['nad-subq', 'bpc-157'],
    contraindications: [
      'organ transplant recipients (may enhance immune rejection)',
      'active autoimmune flare (use with caution — may stimulate immune activity)',
    ],
    drugInteractions: [
      'Immunosuppressants (tacrolimus, cyclosporine) — may counteract their effects',
    ],
    allergenFlags: [],
    pregnancyCategory: 'X — not studied, avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: [
      'CBC with differential at baseline and q8 weeks',
      'Lymphocyte subsets (CD4, CD8, NK cells) if available',
      'Clinical infection frequency tracking',
    ],
    evidenceLevel: 'moderate',
    evidenceSummary:
      'Thymosin alpha-1 (Zadaxin) is approved in 35+ countries for hepatitis B/C and as immune adjuvant. Enhances dendritic cell maturation, T-cell differentiation, and NK cell activity. Used off-label in longevity medicine for immune optimization and chronic infection management (EBV, Lyme). Well-studied safety profile with decades of clinical use.',
    citations: [
      'Tuthill C et al. Thymalfasin: biological properties and clinical applications. Int Immunopharmacol. 2020;85:106580.',
      'Romani L et al. Thymosin alpha1: an endogenous regulator of inflammation, immunity, and tolerance. Ann N Y Acad Sci. 2012;1270:32-36.',
    ],
    gender: 'any',
    ageMin: 25,
    clinicalNotes:
      'Administer subcutaneously in arm or abdomen. Typical protocol: 1.6mg 2x/week for 2-3 months, then reassess. Well tolerated; injection site reactions are rare. May temporarily increase inflammatory markers as immune function improves. Monitor for autoimmune activation in susceptible individuals.',
    rationale:
      'Thymosin alpha-1 is indicated for immune system modulation, enhancing T-cell and NK cell function to address chronic infections and immune senescence.',
    pricing: {
      manufacturingCost: '$20-40/vial',
      supplierCost: '$120-200/mo',
      monthlyPrice: '$449/mo',
    },
    status: 'active',
    sortOrder: 67,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 68. GHK-Cu ---
  {
    id: 'ghk-cu',
    catalogNumber: 68,
    name: 'GHK-Cu',
    ingredients: 'Copper peptide GHK-Cu (glycyl-L-histidyl-L-lysine copper complex)',
    category: 'longevity',
    subcategory: 'skin_regeneration',
    tier: 3,
    route: 'topical',
    dosage: 'Topical: apply to affected area 1-2x/day; SubQ: 1-2mg/day',
    timing: 'PM_RECOVERY',
    triggers: [
      {
        biomarker: 'skin health assessment',
        condition: 'any_suboptimal',
        description: 'Skin aging, wound healing, hair thinning',
      },
    ],
    targetBiomarkers: ['collagen markers', 'wound healing rate'],
    targetGoals: ['longevity', 'body_composition'],
    pairsWith: ['bpc-157', 'epithalon', 'nad-subq'],
    contraindications: ['copper allergy', 'Wilson disease'],
    drugInteractions: [],
    allergenFlags: ['copper'],
    pregnancyCategory: 'Not studied — avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: ['Clinical assessment of skin/wound healing', 'Serum copper if using SubQ route long-term'],
    evidenceLevel: 'moderate',
    evidenceSummary:
      'GHK-Cu is a naturally occurring copper tripeptide that declines with age. Stimulates collagen I, III, and V synthesis, glycosaminoglycan production, and stem cell proliferation. Demonstrated wound healing acceleration, skin remodeling, and hair growth stimulation in clinical studies. Also shows anti-inflammatory gene expression modulation in genomic studies.',
    citations: [
      'Pickart L et al. GHK Peptide as a Natural Modulator of Multiple Cellular Pathways in Skin Regeneration. Biomed Res Int. 2015;2015:648108.',
      'Pickart L, Margolina A. Regenerative and Protective Actions of the GHK-Cu Peptide in the Light of the New Gene Data. Int J Mol Sci. 2018;19(7):1987.',
    ],
    gender: 'any',
    ageMin: 25,
    clinicalNotes:
      'Topical form is most common for skin/hair applications. SubQ injection for systemic regenerative effects. For hair: apply topically to scalp nightly. For wound healing: apply to clean wound bed. Can be compounded into creams, serums, or injectable solutions.',
    rationale:
      'GHK-Cu peptide stimulates collagen synthesis, wound healing, and hair follicle activity through activation of regenerative gene pathways that decline with age.',
    pricing: {
      manufacturingCost: '$10-25/bottle',
      supplierCost: '$60-120/mo',
      monthlyPrice: '$399/mo',
    },
    status: 'active',
    sortOrder: 68,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 69. Epithalon ---
  {
    id: 'epithalon',
    catalogNumber: 69,
    name: 'Epithalon',
    ingredients: 'Epithalon (epitalon, epithalamin tetrapeptide: Ala-Glu-Asp-Gly)',
    category: 'longevity',
    subcategory: 'mtor_longevity',
    tier: 3,
    route: 'subq',
    dosage: '5-10mg subcutaneous daily for 10-20 day cycles',
    timing: 'PM_RECOVERY',
    triggers: [
      {
        biomarker: 'telomere length',
        condition: 'below_functional',
        description: 'Shortened telomere length, advanced biological age',
      },
    ],
    targetBiomarkers: ['telomere length', 'telomerase activity'],
    targetGoals: ['longevity'],
    pairsWith: ['rapamycin', 'nad-subq', 'ghk-cu'],
    contraindications: ['active malignancy (theoretical — telomerase activation in cancer cells)', 'pregnancy'],
    drugInteractions: [],
    allergenFlags: [],
    pregnancyCategory: 'X — not studied, avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: [
      'Telomere length testing at baseline and post-cycle (optional)',
      'Melatonin levels (epithalon may upregulate pineal function)',
      'General wellness markers (CMP, CBC)',
    ],
    evidenceLevel: 'emerging',
    evidenceSummary:
      'Epithalon is a synthetic tetrapeptide analog of epithalamin, derived from the pineal gland. Research by Khavinson et al. demonstrated telomerase activation, telomere elongation, and lifespan extension in animal models. Also shown to restore melatonin secretion in aged subjects. Human data is limited to Russian clinical studies showing improved immune and neuroendocrine function in elderly patients.',
    citations: [
      'Khavinson VKh et al. Peptide Regulation of Gene Expression and Protein Synthesis in Bronchial Epithelium. Lung. 2014;192(5):781-791.',
      'Anisimov VN et al. Effect of Epitalon on biomarkers of aging, life span and spontaneous tumor incidence in female Swiss-derived SHR mice. Biogerontology. 2003;4(4):193-202.',
    ],
    gender: 'any',
    ageMin: 35,
    clinicalNotes:
      'Administered as cyclical protocol: 5-10mg SubQ daily for 10-20 days, then off for 4-6 months. Typically performed 1-2 cycles per year. Inject in abdominal fat pad. Reconstitute lyophilized powder with bacteriostatic water. May improve sleep quality via melatonin pathway activation.',
    rationale:
      'Epithalon is administered in pulsed cycles to activate telomerase and support telomere maintenance as part of an advanced longevity protocol.',
    pricing: {
      manufacturingCost: '$15-30/vial',
      supplierCost: '$100-200/cycle',
      monthlyPrice: '$399/mo',
    },
    status: 'active',
    sortOrder: 69,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 70. SS-31 (Elamipretide) ---
  {
    id: 'ss-31',
    catalogNumber: 70,
    name: 'SS-31 (Elamipretide)',
    ingredients: 'Elamipretide (SS-31, Bendavia, MTP-131)',
    category: 'longevity',
    subcategory: 'mitochondrial_cognitive',
    tier: 3,
    route: 'subq',
    dosage: '40mg subcutaneous daily (research dosing varies)',
    timing: 'AM_ENERGY',
    triggers: [
      {
        biomarker: 'mitochondrial function',
        condition: 'any_suboptimal',
        description: 'Mitochondrial dysfunction, cardiolipin membrane damage',
      },
    ],
    targetBiomarkers: ['mitochondrial function markers', 'exercise capacity', 'cardiac function'],
    targetGoals: ['longevity', 'energy', 'cardiovascular'],
    pairsWith: ['nad-subq', 'methylene-blue', 'rapamycin'],
    contraindications: ['pregnancy', 'severe renal impairment'],
    drugInteractions: [],
    allergenFlags: [],
    pregnancyCategory: 'X — not studied, avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: [
      'Cardiac function assessment if indicated',
      'Exercise capacity testing',
      'CMP at baseline',
      'Renal function monitoring',
    ],
    evidenceLevel: 'emerging',
    evidenceSummary:
      'SS-31 (elamipretide) selectively targets cardiolipin in the inner mitochondrial membrane, stabilizing cristae structure and improving electron transport chain efficiency. Phase 2/3 trials in Barth syndrome and primary mitochondrial myopathy showed functional improvement. Investigated for heart failure and age-related mitochondrial decline. Not yet FDA-approved; available through research/compounding.',
    citations: [
      'Szeto HH. First-in-class cardiolipin-protective compound as a therapeutic agent to restore mitochondrial bioenergetics. Br J Pharmacol. 2014;171(8):2029-2050.',
      'Reid Thompson W et al. A phase 2/3 randomized clinical trial followed by an open-label extension to evaluate the effectiveness of elamipretide in Barth syndrome. Genet Med. 2021;23(3):471-478.',
    ],
    gender: 'any',
    ageMin: 30,
    clinicalNotes:
      'Emerging research compound — limited human long-term safety data. Available through specialty compounding pharmacies. Targets cardiolipin in inner mitochondrial membrane, a novel mechanism distinct from NAD+ or electron carrier approaches. Counsel patient on investigational nature.',
    rationale:
      'SS-31 targets the inner mitochondrial membrane cardiolipin to restore cristae structure and electron transport chain efficiency in age-related mitochondrial decline.',
    pricing: {
      manufacturingCost: '$30-60/vial',
      supplierCost: '$200-400/mo',
      monthlyPrice: '$599/mo',
    },
    status: 'active',
    sortOrder: 70,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 71. Cerebrolysin ---
  {
    id: 'cerebrolysin',
    catalogNumber: 71,
    name: 'Cerebrolysin',
    ingredients: 'Cerebrolysin (porcine brain-derived peptide preparation)',
    category: 'longevity',
    subcategory: 'mitochondrial_cognitive',
    tier: 3,
    route: 'iv',
    dosage: '10-30mL IV or IM daily for 10-20 day cycles',
    timing: 'AM_ENERGY',
    triggers: [
      {
        biomarker: 'cognitive function',
        condition: 'below_functional',
        description: 'Cognitive decline, neurodegenerative conditions, TBI recovery',
      },
    ],
    targetBiomarkers: ['cognitive assessment scores', 'BDNF levels'],
    targetGoals: ['cognition', 'longevity'],
    pairsWith: ['methylene-blue', 'semax', 'nad-subq'],
    contraindications: [
      'epilepsy or seizure disorder',
      'severe renal impairment',
      'allergy to porcine products',
      'pregnancy',
    ],
    drugInteractions: [
      'MAOIs — caution with concurrent use',
      'Antidepressants — may potentiate effects',
    ],
    allergenFlags: ['porcine-derived'],
    pregnancyCategory: 'X — contraindicated',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: [
      'Cognitive function testing pre/post cycle',
      'Renal function (CMP)',
      'Monitor for seizure activity',
      'Vitals during IV administration',
    ],
    evidenceLevel: 'moderate',
    evidenceSummary:
      'Cerebrolysin is a porcine brain-derived peptide preparation containing neurotrophic factors (BDNF-like, NGF-like, CNTF-like activity). Approved in 40+ countries for stroke recovery, TBI, and dementia. Multiple RCTs demonstrate improved cognitive outcomes in Alzheimer disease and vascular dementia. Not FDA-approved in the US. Available through specialty compounding or international sources.',
    citations: [
      'Gauthier S et al. Effects of Cerebrolysin on Neuropsychiatric Symptoms of Alzheimer Disease: An Analysis of Two Large Randomized, Controlled Trials. Dement Geriatr Cogn Disord. 2015;40(3-4):180-192.',
      'Plosker GL, Gauthier S. Cerebrolysin: a review of its use in dementia. Drugs Aging. 2009;26(11):893-915.',
    ],
    gender: 'any',
    ageMin: 30,
    clinicalNotes:
      'Provider-administered only (IV or deep IM). Cyclical protocol: 10-30mL daily for 10-20 days, repeated q3-6 months. IV route preferred for doses >5mL. Administer slowly over 15-30 min. Screen for porcine allergy. Monitor for headache, dizziness (common mild side effects). Not FDA-approved in the US — counsel patient on off-label/international nature.',
    rationale:
      'Cerebrolysin provides neurotrophic factor support for cognitive decline, delivering BDNF-like and NGF-like peptides to promote neuroplasticity and neuronal survival.',
    pricing: {
      manufacturingCost: '$20-50/ampule',
      supplierCost: '$300-600/cycle',
      monthlyPrice: '$599/mo',
    },
    status: 'active',
    sortOrder: 71,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 72. Dihexa ---
  {
    id: 'dihexa',
    catalogNumber: 72,
    name: 'Dihexa',
    ingredients: 'Dihexa (N-hexanoic-Tyr-Ile-(6) aminohexanoic amide)',
    category: 'longevity',
    subcategory: 'mitochondrial_cognitive',
    tier: 3,
    route: 'oral',
    dosage: '10-20mg oral or intranasal daily',
    timing: 'AM_ENERGY',
    triggers: [
      {
        biomarker: 'cognitive function',
        condition: 'below_functional',
        description: 'Cognitive enhancement, HGF pathway activation',
      },
    ],
    targetBiomarkers: ['cognitive assessment scores', 'HGF/c-Met pathway markers'],
    targetGoals: ['cognition', 'longevity'],
    pairsWith: ['semax', 'methylene-blue', 'cerebrolysin'],
    contraindications: [
      'active malignancy (HGF pathway may promote tumor growth)',
      'pregnancy',
      'history of cancer (caution — HGF/c-Met oncogenic potential)',
    ],
    drugInteractions: [],
    allergenFlags: [],
    pregnancyCategory: 'X — not studied, avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: [
      'Cognitive function testing at baseline and q8 weeks',
      'Comprehensive metabolic panel',
      'Cancer screening current per age/gender guidelines',
    ],
    evidenceLevel: 'emerging',
    evidenceSummary:
      'Dihexa is an angiotensin IV analog that potently activates the hepatocyte growth factor (HGF)/c-Met receptor system, promoting synaptogenesis and neuronal connectivity. Preclinical data shows it is 10 million times more potent than BDNF at promoting synaptic connections. No published human clinical trials. Theoretical concern regarding HGF/c-Met pathway role in oncogenesis requires cancer screening before use.',
    citations: [
      'McCoy AT et al. Evaluation of metabolically stabilized angiotensin IV analogs as procognitive/antidementia agents. J Pharmacol Exp Ther. 2013;344(1):141-154.',
      'Benoist CC et al. The procognitive and synaptogenic effects of angiotensin IV-derived peptides are dependent on activation of the hepatocyte growth factor/c-met system. J Pharmacol Exp Ther. 2014;351(2):390-402.',
    ],
    gender: 'any',
    ageMin: 30,
    clinicalNotes:
      'Emerging evidence only — no human RCTs. Available through research chemical suppliers and compounding pharmacies. Oral or intranasal route. Critical consideration: HGF/c-Met pathway is implicated in oncogenesis; ensure up-to-date cancer screening before initiating. Counsel patient thoroughly on investigational nature and theoretical risks.',
    rationale:
      'Dihexa activates the HGF/c-Met pathway to promote synaptogenesis and cognitive enhancement. Prescribed with caution given emerging evidence base and oncogenic pathway concerns.',
    pricing: {
      manufacturingCost: '$15-35/bottle',
      supplierCost: '$80-180/mo',
      monthlyPrice: '$399/mo',
    },
    status: 'active',
    sortOrder: 72,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 73. Semax ---
  {
    id: 'semax',
    catalogNumber: 73,
    name: 'Semax',
    ingredients: 'Semax (heptapeptide, ACTH 4-10 analog: Met-Glu-His-Phe-Pro-Gly-Pro)',
    category: 'longevity',
    subcategory: 'neurohormone',
    tier: 3,
    route: 'nasal',
    dosage: '200-600mcg intranasal daily (divided 2-3 doses)',
    timing: 'AM_ENERGY',
    additionalTimings: ['MIDDAY_ABSORPTION'],
    triggers: [
      {
        biomarker: 'cognitive function',
        condition: 'any_suboptimal',
        description: 'Cognitive optimization, BDNF upregulation, neuroprotection',
      },
    ],
    targetBiomarkers: ['BDNF levels', 'cognitive assessment scores'],
    targetGoals: ['cognition', 'energy', 'longevity'],
    pairsWith: ['selank', 'methylene-blue', 'cerebrolysin'],
    contraindications: [
      'pregnancy',
      'acute psychosis',
      'seizure disorder (use with caution)',
    ],
    drugInteractions: [
      'Stimulants — may potentiate effects (monitor)',
    ],
    allergenFlags: [],
    pregnancyCategory: 'X — not studied, avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: [
      'Cognitive function testing',
      'Blood pressure monitoring (mild pressor effect possible)',
      'Mood and anxiety assessment',
    ],
    evidenceLevel: 'moderate',
    evidenceSummary:
      'Semax is a synthetic ACTH(4-10) analog developed in Russia, approved there for stroke, cognitive disorders, and peptic ulcers. Upregulates BDNF and NGF expression, promotes neuroplasticity, and has neuroprotective effects. Extensive clinical use in Russia with favorable safety profile. Multiple Russian RCTs support cognitive and neuroprotective benefits. Western clinical trial data is limited.',
    citations: [
      'Dolotov OV et al. Semax, an analogue of ACTH(4-10) with cognitive effects, regulates BDNF and trkB expression in the rat hippocampus. Brain Res. 2006;1117(1):54-60.',
      'Gusev EI et al. Neuroprotective effects of semax in acute cerebral ischaemia (clinical and experimental study). Zh Nevrol Psikhiatr Im S S Korsakova. 1997;97(6):26-34.',
    ],
    gender: 'any',
    ageMin: 25,
    clinicalNotes:
      'Intranasal administration provides rapid CNS delivery. Divide daily dose into 2-3 nasal applications. Available as 0.1% or 1% nasal spray. N-Acetyl-Semax-Amidate (NASA) is an enhanced analog with improved bioavailability. Typically cycled 3-4 weeks on, 1-2 weeks off. Non-addictive, non-stimulant cognitive enhancer.',
    rationale:
      'Semax upregulates BDNF expression and promotes neuroplasticity for cognitive optimization and neuroprotection via intranasal delivery.',
    pricing: {
      manufacturingCost: '$10-25/bottle',
      supplierCost: '$60-120/mo',
      monthlyPrice: '$399/mo',
    },
    status: 'active',
    sortOrder: 73,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },

  // --- 74. Selank ---
  {
    id: 'selank',
    catalogNumber: 74,
    name: 'Selank',
    ingredients: 'Selank (heptapeptide, tuftsin analog: Thr-Lys-Pro-Arg-Pro-Gly-Pro)',
    category: 'longevity',
    subcategory: 'neurohormone',
    tier: 3,
    route: 'nasal',
    dosage: '200-400mcg intranasal daily (divided 2-3 doses)',
    timing: 'AM_ENERGY',
    additionalTimings: ['PM_RECOVERY'],
    triggers: [
      {
        biomarker: 'anxiety assessment',
        condition: 'any_suboptimal',
        description: 'Anxiety, immune modulation, neuroprotection',
      },
    ],
    targetBiomarkers: ['anxiety scores', 'immune markers', 'BDNF levels'],
    targetGoals: ['cognition', 'immune', 'longevity'],
    pairsWith: ['semax', 'thymosin-alpha-1', 'nad-subq'],
    contraindications: [
      'pregnancy',
      'active bleeding disorders (tuftsin analog may affect coagulation)',
    ],
    drugInteractions: [
      'Benzodiazepines — may potentiate anxiolytic effects',
      'Anticoagulants — theoretical interaction, monitor',
    ],
    allergenFlags: [],
    pregnancyCategory: 'X — not studied, avoid',
    requiresRx: true,
    requiresProvider: true,
    monitoringRequirements: [
      'Anxiety/mood assessment at baseline and q4 weeks',
      'Immune function markers (optional)',
      'Coagulation panel if on anticoagulants',
    ],
    evidenceLevel: 'moderate',
    evidenceSummary:
      'Selank is a synthetic tuftsin analog developed in Russia, approved there for generalized anxiety disorder. Modulates GABA, serotonin, and dopamine neurotransmission without sedation or dependence. Also demonstrates immunomodulatory effects via tuftsin receptor activation (enhances innate immunity). Extensive Russian clinical data; limited Western clinical trials.',
    citations: [
      'Zozulia AA et al. Anxiolytic and nootropic activity of the Selank peptide anxiolytic. Zh Nevrol Psikhiatr Im S S Korsakova. 2008;108(4):71-75.',
      'Ershov FI et al. Antiviral activity of immunomodulator Selank in experimental influenza infection. Vopr Virusol. 2009;54(5):19-24.',
    ],
    gender: 'any',
    ageMin: 18,
    clinicalNotes:
      'Intranasal administration. Non-sedating anxiolytic — does not impair cognition or cause dependence (unlike benzodiazepines). Pairs well with Semax for complementary cognitive/anxiolytic effects. Available as 0.15% nasal spray. Typical use: daily for 2-4 weeks, then assess. Also has antiviral/immunomodulatory properties via tuftsin pathway.',
    rationale:
      'Selank provides non-sedating anxiolytic and neuroprotective effects via GABA/serotonin modulation, with additional immunomodulatory benefits through the tuftsin receptor pathway.',
    pricing: {
      manufacturingCost: '$10-25/bottle',
      supplierCost: '$60-120/mo',
      monthlyPrice: '$399/mo',
    },
    status: 'active',
    sortOrder: 74,
    updatedAt: '2026-02-12',
    updatedBy: 'formulary-seed',
  },
];
